import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import FeeScheduleBillingCode from "_shared/fee-schedules/categories/billing-codes/fee-schedule-billing-code";
import FeeScheduleCategoryDescription from "_shared/fee-schedules/categories/descriptions/fee-schedule-category-description";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import FeeScheduleCategorySchema, { 
  FeeScheduleCategoryParams, 
  FeeScheduleCategoryJSON 
} from "_shared/fee-schedules/categories/fee-schedule-category-schema";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import Guid from "_shared/guids/guid";
import { IRedlineableField } from "work/proposals/redlining/field-redlines/field-redline";

export type MaskedType<T> = T | Masked | null | undefined;
export type MaskedField = typeof Masked.MaskedField;

export abstract class Masked {
  public static readonly MaskedField = { isMasked: true } as const;

  private static isMaskedJSON(value: unknown): value is typeof Masked.MaskedField {
    return value != null && typeof value === "object" && "isMasked" in value && value.isMasked === true;
  }

  public static isMasked<V>(value: V): value is Extract<V, Masked | typeof Masked.MaskedField> {
    if (!value) return false;
    return value instanceof Masked || Masked.isMaskedJSON(value);
  }

  public static isNotMasked<V>(value: V): value is Exclude<V, Masked | typeof Masked.MaskedField> {
    if (!value) return true;
    return !(value instanceof Masked) && !Masked.isMaskedJSON(value);
  }

  public static isPresentNotMasked<V>(value: V): value is Exclude<NonNullable<V>, Masked | typeof Masked.MaskedField> {
    return value != null && !(value instanceof Masked) && !Masked.isMaskedJSON(value);
  }

  public static assertMasked<V>(value: V): asserts value is Extract<V, Masked | typeof Masked.MaskedField> {
    if (!(value instanceof Masked) && !Masked.isMaskedJSON(value)) {
      throw new Error("Value is not masked");
    }
  }

  public static assertNotMasked<V>(value: V): asserts value is Exclude<V, Masked | typeof Masked.MaskedField> {
    if (value instanceof Masked || Masked.isMaskedJSON(value)) {
      throw new Error("Value is masked");
    }
  }

  public static assertPresentNotMasked<V>(value: V): asserts value is Exclude<NonNullable<V>, Masked> {
    if (value == null || value instanceof Masked || Masked.isMaskedJSON(value)) {
      throw new Error("Value is null/undefined or masked");
    }
  }

  public static isEqual<T extends { isEqualTo(other: T): boolean }>(a: MaskedType<T>, b: MaskedType<T>): boolean {
    // Only compare when both sides are present and not masked
    if (Masked.isPresentNotMasked(a) && Masked.isPresentNotMasked(b)) {
      return a.isEqualTo(b);
    }
    // If either side is masked (or missing), treat as equal
    // so it doesn't cause inequality
    return true;
  }

  public static isOptionalEqual<T extends { isEqualTo(other: T): boolean }>(
    a: MaskedType<T>,
    b: MaskedType<T>
  ): boolean {
    // If both are present and not masked, compare real values
    if (Masked.isPresentNotMasked(a) && Masked.isPresentNotMasked(b)) {
      return a.isEqualTo(b);
    }
    // If exactly one side is present and not masked, they differ
    if (Masked.isPresentNotMasked(a) !== Masked.isPresentNotMasked(b)) {
      return false;
    }
    // Otherwise (both absent, or at least one masked), treat as equal/ignored
    return true;
  }

  public static isUnorderedArrayEqual<T extends { isEqualTo(other: T): boolean }>(
    a: T[] | Masked | undefined,
    b: T[] | Masked | undefined
  ): boolean {
    // If either is masked, ignore differences
    if (Masked.isMasked(a) || Masked.isMasked(b)) return true;

    // Now both sides are either arrays or undefined
    if (!a || !b) return !a && !b;

    if (a.length !== b.length) return false;
    // Unordered: every a has a match in b and vice versa
    return a.every((ax) => b.some((bx) => bx.isEqualTo(ax))) && b.every((bx) => a.some((ax) => ax.isEqualTo(bx)));
  }

  public toJSON = () => Masked.MaskedField;
  public clone = () => this;
  public isEqualTo = (other: object | null | undefined): boolean => other instanceof Masked;
}

export class FeeScheduleCategoryMaskedCurrency implements IRedlineableField {
  public static isNotMasked(
    category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency
  ): category is FeeScheduleCategory {
    return !(category instanceof FeeScheduleCategoryMaskedCurrency);
  }

  public static create(params: FeeScheduleCategoryParams): FeeScheduleCategoryMaskedCurrency {
    const parsed = FeeScheduleCategorySchema.validate(params);
    return new FeeScheduleCategoryMaskedCurrency(parsed);
  }

  public static fromJSON(json: FeeScheduleCategoryJSON): FeeScheduleCategoryMaskedCurrency {
    if (isNullOrUndefined(json)) return json;
    const params = FeeScheduleCategorySchema.deserialize(json);
    return new FeeScheduleCategoryMaskedCurrency(params);
  }

  public readonly id: Guid;
  public readonly name: FeeScheduleCategoryName;
  public readonly description?: FeeScheduleCategoryDescription | null;
  public readonly billingCode?: FeeScheduleBillingCode | null;

  public get isEmpty(): boolean {
    // A FeeScheduleCategoryMaskedCurrency is never considered empty, required by IRedlineableField
    return false;
  }

  public get feeString(): string {
    return `(Masked Fee)`;
  }
  
  public constructor(params: FeeScheduleCategoryParams) {
    this.id = params.id ?? Guid.generate();
    this.name = params.name;
    this.description = params.description;
    this.billingCode = params.billingCode;
  }

  public toString(): string {
    let feeString: string;
    feeString = (this.description ? `${this.name.value}: ${this.description.toString()}\n` : "");
    if (this.billingCode) {
      feeString += `, Billing Code: ${this.billingCode.toString()}`;
    }
    return feeString;
  }

  public toJSON(): FeeScheduleCategoryJSON {
    return FeeScheduleCategorySchema.serialize(this);
  }

  public isEqualTo(other: object | null | undefined): boolean {
    if (!other) return false;
    if (!(other instanceof FeeScheduleCategoryMaskedCurrency)) return false;
    return (Boolean(
      this.name?.isEqualTo(other.name) &&
      this.description?.isEqualTo(other.description)
    ));
  }
}

export class MaskedTeam extends Masked {}
export class MaskedFeeSchedule extends Masked {}
export class MaskedContracts extends Masked {}
export class MaskedDiscount extends Masked {}
